/**
 * Shared inline styles for the Trace Studio panel.
 */

import type React from 'react';

// Inject spin keyframe for loading spinners (idempotent)
if (typeof document !== 'undefined' && !document.querySelector('style[data-catalog-keyframes]')) {
    const s = document.createElement('style');
    s.setAttribute('data-catalog-keyframes', '');
    s.textContent = '@keyframes spin{to{transform:rotate(360deg)}}';
    document.head.appendChild(s);
}

export const styles = {
    container: {
        padding: '8px',
        color: 'var(--colorNeutralForeground1)',
        fontSize: '12px',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
    } as React.CSSProperties,
    header: {
        display: 'flex',
        alignItems: 'center',
        gap: '8px',
        marginBottom: '4px',
        flexShrink: 0,
    } as React.CSSProperties,
    backButton: {
        background: 'none',
        border: 'none',
        color: 'var(--colorNeutralForeground3)',
        cursor: 'pointer',
        padding: '4px',
        fontSize: '14px',
        lineHeight: 1,
        borderRadius: '3px',
    } as React.CSSProperties,
    title: {
        fontSize: '13px',
        fontWeight: 600,
        color: 'var(--colorNeutralForeground1)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as React.CSSProperties,
    searchInput: {
        padding: '5px 8px',
        backgroundColor: 'var(--colorNeutralBackground3)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        color: 'var(--colorNeutralForeground1)',
        fontSize: '12px',
        outline: 'none',
        width: '100%',
        marginBottom: '6px',
        flexShrink: 0,
    } as React.CSSProperties,
    flexGrid: {
        display: 'flex',
        flexWrap: 'wrap',
        gap: '6px',
        overflowY: 'auto',
        flex: 1,
        alignContent: 'flex-start',
        paddingBottom: '8px',
    } as React.CSSProperties,
    card: {
        backgroundColor: 'var(--colorNeutralBackground3)',
        borderRadius: '4px',
        cursor: 'pointer',
        overflow: 'hidden',
        border: '2px solid transparent',
        transition: 'border-color 0.15s',
        flexShrink: 0,
    } as React.CSSProperties,
    cardLabel: {
        padding: '4px 6px',
        fontSize: '11px',
        color: 'var(--colorNeutralForeground2)',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap',
    } as React.CSSProperties,
    center: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        height: '100%',
        color: 'var(--colorNeutralForeground4)',
        fontSize: '12px',
        textAlign: 'center',
        padding: '16px',
    } as React.CSSProperties,
    loginForm: {
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        padding: '16px',
        maxWidth: '240px',
        margin: '0 auto',
    } as React.CSSProperties,
    input: {
        padding: '6px 8px',
        backgroundColor: 'var(--colorNeutralBackground3)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        color: 'var(--colorNeutralForeground1)',
        fontSize: '12px',
        outline: 'none',
    } as React.CSSProperties,
    loginButton: {
        padding: '6px 12px',
        backgroundColor: 'var(--colorBrandBackground)',
        border: 'none',
        borderRadius: '3px',
        color: 'var(--colorNeutralForegroundOnBrand)',
        cursor: 'pointer',
        fontSize: '12px',
        fontWeight: 600,
    } as React.CSSProperties,
    loginError: {
        color: 'var(--colorPaletteRedForeground1)',
        fontSize: '11px',
        textAlign: 'center',
    } as React.CSSProperties,
    logoutButton: {
        background: 'none',
        border: 'none',
        color: 'var(--colorNeutralForeground4)',
        cursor: 'pointer',
        padding: '4px',
        fontSize: '11px',
        marginLeft: 'auto',
        flexShrink: 0,
    } as React.CSSProperties,
    errorRetry: {
        marginTop: '8px',
        padding: '4px 12px',
        backgroundColor: 'var(--colorNeutralBackground3)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        color: 'var(--colorNeutralForeground1)',
        cursor: 'pointer',
        fontSize: '11px',
    } as React.CSSProperties,
    tabBar: {
        display: 'flex',
        gap: '0px',
        marginBottom: '6px',
        flexShrink: 0,
        borderBottom: '1px solid var(--colorNeutralStroke2)',
    } as React.CSSProperties,
    tab: (active: boolean): React.CSSProperties => ({
        background: 'none',
        border: 'none',
        borderBottom: active ? '2px solid var(--colorBrandBackground)' : '2px solid transparent',
        color: active ? 'var(--colorNeutralForeground1)' : 'var(--colorNeutralForeground3)',
        cursor: 'pointer',
        padding: '6px 12px',
        fontSize: '12px',
        fontWeight: active ? 600 : 400,
        transition: 'color 0.15s, border-color 0.15s',
    }),
    select: {
        padding: '5px 8px',
        backgroundColor: 'var(--colorNeutralBackground3)',
        border: '1px solid var(--colorNeutralStroke1)',
        borderRadius: '3px',
        color: 'var(--colorNeutralForeground1)',
        fontSize: '12px',
        outline: 'none',
        width: '100%',
        marginBottom: '6px',
        flexShrink: 0,
    } as React.CSSProperties,
};
